// AI-suggested domain knowledge — surfaced on every finished discovery
// whenever the backend produced a non-null `suggested_knowledge`. v2 of
// the card supports three independent sections (tiers, edges_required,
// edges_forbidden); any subset can be present. The legacy v1 gate that
// hid the card when `knowledge_id` was already set is gone — refining
// existing knowledge is a first-class use case now.
//
// Defensive contract: the backend payload is treated as untrusted. Each
// section is validated independently; invalid sections are dropped
// silently (console.warn for debugging) and the card still renders if
// any OTHER section survives validation. When every section ends up
// empty the card returns null — the user must never see a half-broken
// suggestion that would Apply-into a server-side 422.
//
// Idempotency: a single crypto.randomUUID is memoized per discovery id
// and reused for the discovery POST so a double-click can't enqueue two
// runs.

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useCreateDiscovery } from '@/hooks/useDiscoveries';
import { useCreateKnowledge, useKnowledgeList } from '@/hooks/useKnowledge';
import { useAuth } from '@/hooks/useAuth';
import { extractApiError } from '@/lib/api';
import type {
  Discovery,
  SuggestedEdge,
  SuggestedKnowledge,
  SuggestedTier,
} from '@/lib/discoveries';
import { friendlyError } from '@/lib/error-messages';
import type { KnowledgeKind, KnowledgeObject } from '@/lib/knowledge';
import { ArrowRight, Ban, Layers, Sparkles } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface SuggestedKnowledgeCardProps {
  discovery: Discovery;
  // The current discovery's `result.nodes` — variable names actually
  // present in the graph. Used to validate that the suggestion references
  // real variables before rendering.
  resultNodes: string[];
}

type ApplyState = 'idle' | 'creating-knowledge' | 'creating-discovery';

interface ValidationResult {
  validTiers: SuggestedTier[];
  validRequired: SuggestedEdge[];
  validForbidden: SuggestedEdge[];
  hasRationale: boolean;
}

// Defensive per-section validation. Returns the cleaned sub-set of each
// section so the card can render whatever survives even if siblings are
// invalid. Logs once on console.warn for any rejection so we can spot
// bad backend payloads in production without crashing the page.
function validateSuggestion(
  suggestion: SuggestedKnowledge | null | undefined,
  resultNodes: string[],
): ValidationResult {
  const empty: ValidationResult = {
    validTiers: [],
    validRequired: [],
    validForbidden: [],
    hasRationale: false,
  };
  if (!suggestion) return empty;

  const hasRationale =
    typeof suggestion.rationale === 'string' && suggestion.rationale.trim() !== '';
  if (!hasRationale) {
    console.warn('suggested_knowledge.invalid_rationale', suggestion.rationale);
    return empty;
  }

  const nodeSet = new Set(resultNodes);

  // --- Tiers ---------------------------------------------------------
  // Validate tiers as a group: pairwise-disjoint, all variables ⊂ nodes,
  // at least one non-empty label. A single tier is useless on its own
  // (no ordering signal); we silently drop the tiers section in that
  // case but the card still renders if edges survive.
  let validTiers: SuggestedTier[] = [];
  const rawTiers = Array.isArray(suggestion.tiers) ? suggestion.tiers : [];
  if (rawTiers.length >= 2) {
    const seen = new Set<string>();
    const missing: string[] = [];
    let duplicate: string | null = null;
    let bad = false;
    for (const tier of rawTiers) {
      if (!Array.isArray(tier?.variables)) {
        bad = true;
        break;
      }
      for (const v of tier.variables) {
        if (!nodeSet.has(v)) missing.push(v);
        if (seen.has(v)) {
          duplicate = v;
          break;
        }
        seen.add(v);
      }
      if (duplicate) break;
    }
    if (bad) {
      console.warn('suggested_knowledge.invalid_tiers_shape', rawTiers);
    } else if (duplicate) {
      console.warn('suggested_knowledge.duplicate_variables', duplicate);
    } else if (missing.length > 0) {
      console.warn('suggested_knowledge.invalid_variables', missing);
    } else {
      const hasNonEmptyLabel = rawTiers.some(
        (t) => typeof t.label === 'string' && t.label.trim() !== '',
      );
      if (!hasNonEmptyLabel) {
        console.warn('suggested_knowledge.invalid_tier_labels', rawTiers);
      } else {
        validTiers = rawTiers;
      }
    }
  } else if (rawTiers.length === 1) {
    console.warn('suggested_knowledge.single_tier_ignored', rawTiers);
  }

  // --- Edges helper --------------------------------------------------
  function validateEdges(
    raw: SuggestedEdge[] | undefined,
    label: 'edges_required' | 'edges_forbidden',
  ): SuggestedEdge[] {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const out: SuggestedEdge[] = [];
    const seen = new Set<string>();
    for (const edge of raw) {
      if (
        !edge ||
        typeof edge.src !== 'string' ||
        typeof edge.dst !== 'string' ||
        edge.src === edge.dst ||
        !nodeSet.has(edge.src) ||
        !nodeSet.has(edge.dst)
      ) {
        console.warn(`suggested_knowledge.invalid_${label}`, edge);
        continue;
      }
      const key = `${edge.src} ${edge.dst}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ src: edge.src, dst: edge.dst });
    }
    return out;
  }

  const validRequired = validateEdges(suggestion.edges_required, 'edges_required');
  const validForbidden = validateEdges(suggestion.edges_forbidden, 'edges_forbidden');

  // edges_required and edges_forbidden must be disjoint as (src,dst)
  // pairs. If overlap is found, drop the whole forbidden section — it's
  // semantically broken to both lock in and block the same direction.
  if (validRequired.length > 0 && validForbidden.length > 0) {
    const reqSet = new Set(validRequired.map((e) => `${e.src} ${e.dst}`));
    const conflict = validForbidden.some((e) => reqSet.has(`${e.src} ${e.dst}`));
    if (conflict) {
      console.warn('suggested_knowledge.required_forbidden_overlap', {
        validRequired,
        validForbidden,
      });
      return {
        validTiers,
        validRequired,
        validForbidden: [],
        hasRationale,
      };
    }
  }

  return { validTiers, validRequired, validForbidden, hasRationale };
}

// Version-numbered + tagged name: `<datasetLabel> · v3 (AI refinement) · 2026-…`.
// We compute the version from the count of existing knowledge_objects attached
// to this dataset — first AI suggestion is v1, each Apply & re-run is v(N+1).
// Truncated to 200 chars (backend max-length on KO.name).
function defaultKnowledgeName(
  datasetLabel: string,
  version: number,
  isRefinement: boolean,
): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const kind = isRefinement ? 'AI refinement' : 'AI suggestion';
  const name = `${datasetLabel} · v${version} (${kind}) · ${stamp}`;
  return name.length > 200 ? name.slice(0, 200) : name;
}

export function SuggestedKnowledgeCard({
  discovery,
  resultNodes,
}: SuggestedKnowledgeCardProps) {
  const navigate = useNavigate();
  const { quota } = useAuth();
  const createKnowledge = useCreateKnowledge();
  const createDiscovery = useCreateDiscovery();
  const [state, setState] = React.useState<ApplyState>('idle');

  // Quota pre-check: Apply & re-run creates a KO *and* spends a discovery.
  // If the discovery quota is exhausted, stop BEFORE creating an orphan
  // knowledge object — show a clear message + Upgrade path instead of
  // letting the backend 402 mid-flow. null quota = unknown → allow.
  const quotaExhausted =
    quota != null &&
    ((quota.period_cap != null && quota.used_this_period >= quota.period_cap) ||
      (quota.lifetime_cap != null && quota.lifetime_used >= quota.lifetime_cap));

  // One key per discovery id, memoized for the lifetime of this mount.
  // Reused for the discovery POST so a double-click maps to the same
  // server-side row (backend returns 409 idempotency.replay which we
  // treat as success below).
  const idempotencyKey = React.useMemo(
    () => crypto.randomUUID(),
    // biome-ignore lint/correctness/useExhaustiveDependencies: discovery.id is the intentional cache key
    [discovery.id],
  );

  const { validTiers, validRequired, validForbidden, hasRationale } = React.useMemo(
    () => validateSuggestion(discovery.suggested_knowledge, resultNodes),
    [discovery.suggested_knowledge, resultNodes],
  );

  const hasAnySection =
    validTiers.length >= 2 || validRequired.length > 0 || validForbidden.length > 0;

  if (!discovery.suggested_knowledge || !hasRationale || !hasAnySection) {
    return null;
  }
  const { rationale } = discovery.suggested_knowledge;

  const datasetLabel = discovery.dataset_name ?? discovery.dataset_id;
  // Count existing knowledge_objects for this dataset to pick the next
  // version number. While loading we fall back to `?` in the displayed
  // name (the button stays enabled; if the user clicks before the count
  // arrives, we re-fetch in onApply so the saved KO still gets the right
  // version).
  const koListForDataset = useKnowledgeList({ dataset_id: discovery.dataset_id });
  const existingCount = koListForDataset.data?.items?.length;
  const isRefinement = !!discovery.knowledge_id;
  const nextVersion = (existingCount ?? 0) + 1;
  const koName = defaultKnowledgeName(datasetLabel, nextVersion, isRefinement);
  const title = isRefinement
    ? 'Refine the domain knowledge'
    : 'Add domain knowledge to sharpen this graph';

  const isBusy = state !== 'idle';

  const primaryLabel =
    state === 'creating-knowledge'
      ? 'Creating knowledge…'
      : state === 'creating-discovery'
        ? 'Re-running discovery…'
        : quotaExhausted
          ? 'Upgrade to re-run'
          : 'Apply & re-run';

  async function onApply() {
    // Pre-flight quota gate — don't create a knowledge object we can't
    // act on. Send the user straight to billing instead.
    if (quotaExhausted) {
      toast.error('Your free quota is used up — upgrade your plan to run more discoveries.');
      navigate('/billing');
      return;
    }
    const kinds: KnowledgeKind[] = [];
    if (validTiers.length >= 2) kinds.push('tiers');
    if (validRequired.length > 0) kinds.push('edges_required');
    if (validForbidden.length > 0) kinds.push('edges_forbidden');
    if (kinds.length === 0) {
      // Defensive — shouldn't happen since the early-return above blocks
      // an empty-section render path. But abort cleanly rather than ship
      // an empty payload that the backend will 422.
      console.warn('suggested_knowledge.apply_empty_kinds');
      return;
    }

    setState('creating-knowledge');

    let ko: KnowledgeObject;
    try {
      ko = await createKnowledge.mutateAsync({
        name: koName,
        dataset_id: discovery.dataset_id,
        kinds,
        payload: {
          tiers: validTiers.map((t) => t.variables),
          edges_required: validRequired.map((e) => [e.src, e.dst] as [string, string]),
          edges_forbidden: validForbidden.map((e) => [e.src, e.dst] as [string, string]),
        },
      });
    } catch (err) {
      const apiErr = extractApiError(err);
      if (apiErr?.code === 'knowledge.empty_payload') {
        toast.error(
          "Couldn't save the suggestion — it was empty. Try Customize first.",
        );
      } else {
        toast.error(
          `Couldn't save the suggestion. ${friendlyError(
            apiErr?.code,
            apiErr?.message ?? 'Unexpected error.',
          )}`,
        );
      }
      setState('idle');
      return;
    }

    setState('creating-discovery');

    try {
      const job = await createDiscovery.mutateAsync({
        body: {
          dataset_id: discovery.dataset_id,
          knowledge_id: ko.id,
          params: { seed: discovery.params.seed ?? 42 },
        },
        options: { idempotencyKey },
      });
      toast.success('Re-running with the new knowledge — opening the new discovery.');
      navigate(`/discoveries/${job.id}`);
    } catch (err) {
      const apiErr = extractApiError(err);
      if (apiErr?.code === 'quota.exhausted' || apiErr?.code === 'quota.exceeded') {
        toast.error(
          'Your free quota is used up. The knowledge was saved — open Knowledge to re-run later, or upgrade your plan.',
        );
        setState('idle');
        navigate(`/knowledge/${ko.id}/edit`);
        return;
      }
      if (apiErr?.code === 'idempotency.replay') {
        const existingId =
          (apiErr.metadata?.existing_id as string | undefined) ??
          (apiErr.metadata?.discovery_id as string | undefined);
        if (existingId) {
          toast.success('Re-running with the new knowledge — opening the new discovery.');
          navigate(`/discoveries/${existingId}`);
          return;
        }
        navigate('/discoveries');
        return;
      }
      toast.error(
        `Saved the knowledge, but couldn't start the re-run. ${friendlyError(
          apiErr?.code,
          apiErr?.message ?? 'Unexpected error.',
        )}`,
      );
      toast.message('Open Knowledge to retry', {
        action: {
          label: 'Open Knowledge →',
          onClick: () => navigate(`/knowledge/${ko.id}/edit`),
        },
      });
      setState('idle');
    }
  }

  function onCustomize() {
    navigate('/knowledge/new', {
      state: {
        prefill: {
          datasetId: discovery.dataset_id,
          name: koName,
          tiers: validTiers.map((t) => t.variables),
          tierLabels: validTiers.map((t) => t.label),
          edges_required: validRequired,
          edges_forbidden: validForbidden,
          rerunDiscovery: {
            dataset_id: discovery.dataset_id,
            seed: discovery.params.seed ?? 42,
          },
        },
      },
    });
  }

  return (
    <section aria-labelledby="suggested-knowledge-title" aria-busy={isBusy}>
      <Card>
        <CardHeader>
          <CardTitle
            id="suggested-knowledge-title"
            className="flex items-center gap-2 text-base"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">{rationale}</p>

          {validTiers.length >= 2 ? (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                Suggested tiers (top → bottom: causes flow downward):
              </p>
              <ul className="space-y-1.5">
                {validTiers.map((tier, idx) => (
                  <li
                    key={`${tier.label}-${idx}`}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <Badge variant="secondary">{tier.label || `Tier ${idx + 1}`}</Badge>
                    <span className="font-mono text-sm">{tier.variables.join(' · ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {validRequired.length > 0 ? (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                Lock in these directions (the AI is confident):
              </p>
              <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
                {validRequired.map((e, idx) => (
                  <li key={`req-${e.src}-${e.dst}-${idx}`}>
                    <code className="font-mono text-sm">
                      {e.src} → {e.dst}
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {validForbidden.length > 0 ? (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                <Ban className="h-3.5 w-3.5" aria-hidden="true" />
                Block these directions (likely incorrect):
              </p>
              <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
                {validForbidden.map((e, idx) => (
                  <li key={`forb-${e.src}-${e.dst}-${idx}`}>
                    <code className="font-mono text-sm line-through opacity-70">
                      {e.src} → {e.dst}
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button onClick={onApply} disabled={isBusy} aria-busy={isBusy}>
              {primaryLabel}
            </Button>
            <Button variant="ghost" onClick={onCustomize} disabled={isBusy}>
              Customize first
            </Button>
          </div>
        </CardContent>
        <CardFooter>
          <p className="text-xs text-muted-foreground">
            Suggested by the AI from your variable names — review before applying. Costs 1
            discovery from your quota.
          </p>
        </CardFooter>
      </Card>
    </section>
  );
}

export default SuggestedKnowledgeCard;
